// src/components/Home/HomePage.js
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import ProjectCard from "./ProjectCard";
import ProjectModal from "./ProjectModal";

import {
  Container,
  Typography,
  Box,
  Button,
  Grid,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CheckIcon from "@mui/icons-material/Check";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

// Sanity
import { sanity } from "../../lib/sanity";
import { projectsQuery } from "../../groq/projects";

// ✅ Import hero background images (placed in src/assets/projects/)
import projectBg1 from "../../assets/projects/project11.png";
import projectBg2 from "../../assets/projects/project22.png";
import projectBg3 from "../../assets/projects/project33.jpg";

// --- Styled Components ---
const HeroSection = styled(Box)(() => ({
  position: "relative",
  minHeight: "85vh",
  display: "flex",
  alignItems: "center",
  overflow: "hidden",
  backgroundColor: "#1A1A1A",
}));

const CarouselContainer = styled(Box)(() => ({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  zIndex: 1,
}));

const CarouselSlide = styled("div")(() => ({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  backgroundSize: "cover",
  backgroundPosition: "center",
  transition: "opacity 1.5s ease-in-out, transform 6s ease",
  transform: "scale(1.05)", // subtle cinematic zoom
  "&::after": {
    content: '""',
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(to right, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.5) 50%, rgba(0,0,0,0.3) 100%)",
    pointerEvents: "none",
  },
}));

const HeroContent = styled(Container)(() => ({
  position: "relative",
  zIndex: 3,
  maxWidth: "1200px",
  padding: "0 24px",
}));

const CTAButton = styled(Button)(() => ({
  backgroundColor: "#D2691E",
  color: "#FFFFFF",
  fontSize: "1.1rem",
  padding: "14px 36px",
  borderRadius: "6px",
  fontWeight: 600,
  textTransform: "none",
  "&:hover": {
    backgroundColor: "#E08D4C",
  },
}));

const WhoWeAreSection = styled(Box)(({ theme }) => ({
  backgroundColor: "#FFFFFF",
  padding: theme.spacing(10, 0),
}));

const ProjectsSection = styled(Box)(({ theme }) => ({
  backgroundColor: "#FAF7F2",
  padding: theme.spacing(10, 0),
}));

const FinancingBanner = styled(Box)(({ theme }) => ({
  backgroundColor: "#2C2416",
  padding: theme.spacing(4, 0),
  color: "#FFFFFF",
  width: "100%",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(3, 0),
  },
}));

const FinancingBannerContainer = styled(Container)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: theme.spacing(2),
  maxWidth: "1400px",
  padding: theme.spacing(0, 4),
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    textAlign: "center",
    padding: theme.spacing(0, 3),
    gap: theme.spacing(2.5),
  },
}));

const FinancingText = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: 12,
  [theme.breakpoints.down("sm")]: {
    flexDirection: "row",
    justifyContent: "center",
    gap: 10,
    width: "100%",
    flexWrap: "wrap",
  },
}));

const PhoneButton = styled(Button)(({ theme }) => ({
  backgroundColor: "#D2691E",
  color: "#FFFFFF",
  textTransform: "none",
  fontWeight: 600,
  padding: "12px 24px",
  borderRadius: 24,
  fontSize: "1rem",
  "&:hover": {
    backgroundColor: "#E08D4C",
    transform: "translateY(-2px)",
    boxShadow: "0 4px 12px rgba(210, 105, 30, 0.3)",
  },
  transition: "all 0.3s ease",
  [theme.breakpoints.down("sm")]: {
    width: "100%",
    padding: "14px 24px",
    fontSize: "1.125rem",
    maxWidth: "300px",
  },
}));

// Facebook Brand Logo SVG - Official Facebook Blue
const FacebookLogo = ({ sx = {} }) => (
  <Box sx={{ display: "inline-flex", ...sx }}>
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: "block" }}
    >
      <path
        d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
        fill="#1877F2"
      />
    </svg>
  </Box>
);

// Instagram Brand Logo SVG - Official Instagram Gradient
const InstagramLogo = ({ sx = {} }) => {
  const gradientId = `instagram-gradient-${Math.random().toString(36).substr(2, 9)}`;
  return (
    <Box sx={{ display: "inline-flex", ...sx }}>
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ display: "block" }}
      >
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#E4405F" />
            <stop offset="50%" stopColor="#FCAF45" />
            <stop offset="100%" stopColor="#833AB4" />
          </linearGradient>
        </defs>
        <path
          d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"
          fill={`url(#${gradientId})`}
        />
      </svg>
    </Box>
  );
};

const SocialIconButton = styled(Button)(({ theme }) => ({
  minWidth: "48px",
  width: "48px",
  height: "48px",
  padding: 0,
  borderRadius: "50%",
  backgroundColor: "transparent",
  border: "2px solid rgba(255, 255, 255, 0.3)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    borderColor: "rgba(255, 255, 255, 0.5)",
    transform: "translateY(-2px)",
  },
  transition: "all 0.3s ease",
  [theme.breakpoints.down("sm")]: {
    minWidth: "44px",
    width: "44px",
    height: "44px",
  },
}));

const SocialIconsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  [theme.breakpoints.down("sm")]: {
    gap: theme.spacing(1),
  },
}));

const BannerActionsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    width: "100%",
    gap: theme.spacing(2),
    alignItems: "center",
    "& > *:last-child": {
      width: "100%",
      maxWidth: "300px",
    },
  },
}));

const ProjectsContainer = styled(Box)(({ theme }) => ({
  maxWidth: "1400px",
  margin: "0 auto",
  padding: "0 24px",
  [theme.breakpoints.down("sm")]: {
    padding: "0 16px",
  },
}));

const ProjectsGrid = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  margin: "0 -12px",
  width: "calc(100% + 24px)",
  "& .project-card-wrapper": {
    padding: "0 12px",
    marginBottom: "24px",
    [theme.breakpoints.up("md")]: {
      width: "33.333%",
      flexBasis: "33.333%",
    },
    [theme.breakpoints.between("sm", "md")]: {
      width: "50%",
      flexBasis: "50%",
    },
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      flexBasis: "100%",
    },
  },
}));

const FeatureItem = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  marginBottom: 16,
  color: "#2C2416",
}));

const LearnMoreButton = styled(Button)(() => ({
  backgroundColor: "#6B4423",
  color: "#FFFFFF",
  padding: "12px 28px",
  marginTop: 24,
  "&:hover": { backgroundColor: "#8B5A3C" },
}));

const HomePage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const [displayProjects, setDisplayProjects] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [visibleCount, setVisibleCount] = useState(6);

  // Scroll to top when component mounts or location changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // ✅ Use imported images in the carousel
  const backgroundImages = [projectBg1, projectBg2, projectBg3];

  // Carousel auto-advance
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % backgroundImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [backgroundImages.length]);

  // Fetch Sanity projects
  useEffect(() => {
    let sub;
    const fetchProjects = async () => {
      try {
        const data = await sanity.fetch(projectsQuery);
        setDisplayProjects(data || []);
      } catch (e) {
        console.error("Projects fetch error:", e);
      }
    };
    fetchProjects();

    sub = sanity.listen(`*[_type=="project"]`).subscribe({
      next: () => fetchProjects(),
      error: (err) => console.error("Sanity listen error:", err),
    });

    return () => sub && sub.unsubscribe();
  }, []);

  const handleCardClick = (project) => {
    setSelectedProject(project);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedProject(null);
  };

  // Redirect to Contact page
  const goToContactPage = () => navigate("/contact");

  // Social media handlers
  const handleFacebookClick = () => {
    window.open(
      "https://www.facebook.com/share/1C2CypNkYM/?mibextid=wwXIfr",
      "_blank",
      "noopener,noreferrer"
    );
  };

  const handleInstagramClick = () => {
    window.open(
      "https://www.instagram.com/sdsoln?igsh=MTRkM3FsZDBrbDl3OA==",
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <>
      {/* Hero Section */}
      <HeroSection>
        <CarouselContainer>
          {backgroundImages.map((img, i) => (
            <CarouselSlide
              key={i}
              style={{
                backgroundImage: `url(${img})`,
                opacity: currentSlide === i ? 1 : 0,
              }}
            />
          ))}
        </CarouselContainer>

        <HeroContent>
          <Typography
            variant="h1"
            sx={{
              color: "#FFF",
              fontWeight: 700,
              fontSize: { xs: "2.8rem", md: "4rem" },
              mb: 3,
            }}
          >
            Texas Homes. <br /> Built Better.
          </Typography>
          <Typography
            variant="h6"
            sx={{ color: "#FFF", mb: 4, maxWidth: "600px" }}
          >
            Every great space begins with a Dream. 
            <br />
            You IMAGINE, We DESIGN & REMODEL it. 
            <br />
            Building spaces and relationships that last a lifetime.
          </Typography>
          <CTAButton endIcon={<ArrowForwardIcon />} onClick={goToContactPage}>
            Get a Free Quote
          </CTAButton>
        </HeroContent>
      </HeroSection>

      {/* Who We Are Section */}
      <WhoWeAreSection>
        <Container>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h2"
                component="h2"
                sx={{ color: "#2C2416", fontWeight: 700, mb: 3 }}
              >
                Who We Are
              </Typography>
              <Typography variant="body1" sx={{ color: "#5C4033", mb: 1, lineHeight: 1.6 }}>
              At SD Solutions, we're passionate about transforming homes…
              </Typography>
              <Typography variant="body1" sx={{ color: "#5C4033", mb: 3, lineHeight: 1.6 }}>
              Our commitment is built on transparency, open communication, and delivering results that exceed expectations…
              </Typography>
              <Box sx={{ mb: 4 }}>
                <FeatureItem>
                  <CheckIcon sx={{ color: "#D2691E", mr: 2 }} />
                  <Typography variant="body1" sx={{ color: "#2C2416", fontWeight: 600 }}>
                  Your Local Experts: Deeply familiar with current home styles and trends.
                  </Typography>
                </FeatureItem>
                <FeatureItem>
                  <CheckIcon sx={{ color: "#D2691E", mr: 2 }} />
                  <Typography variant="body1" sx={{ color: "#2C2416", fontWeight: 600 }}>
                  End-to-End Service: We manage every step, from design concepts to final results.
                  </Typography>
                </FeatureItem>
                <FeatureItem>
                  <CheckIcon sx={{ color: "#D2691E", mr: 2 }} />
                  <Typography variant="body1" sx={{ color: "#2C2416", fontWeight: 600 }}>
                  Personalized Attention: Custom-crafted solutions designed around your unique vision.
                  </Typography>
                </FeatureItem>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </WhoWeAreSection>

      {/* Financing Banner */}
      <FinancingBanner>
        <FinancingBannerContainer>
          <FinancingText>
            <CheckCircleIcon 
              sx={{ 
                color: "#4CAF50", 
                fontSize: { xs: 24, sm: 28 },
                flexShrink: 0,
              }} 
            />
            <Typography 
              variant="h6" 
              sx={{ 
                color: "#FFFFFF", 
                fontWeight: 500,
                fontSize: { xs: "0.95rem", sm: "1.25rem" },
                lineHeight: { xs: 1.4, sm: 1.5 },
              }}
            >
              Planning a project? Get expert guidance today
            </Typography>
          </FinancingText>
          <BannerActionsContainer>
            <SocialIconsContainer>
              <SocialIconButton
                onClick={handleFacebookClick}
                aria-label="Follow us on Facebook"
              >
                <FacebookLogo />
              </SocialIconButton>
              <SocialIconButton
                onClick={handleInstagramClick}
                aria-label="Follow us on Instagram"
              >
                <InstagramLogo />
              </SocialIconButton>
            </SocialIconsContainer>
            <PhoneButton
              onClick={() => {
                window.location.href = "tel:+15082413648";
              }}
            >
              +1 (508) 241-3648
            </PhoneButton>
          </BannerActionsContainer>
        </FinancingBannerContainer>
      </FinancingBanner>

      {/* Projects Section */}
      <ProjectsSection>
        <ProjectsContainer>
          <Typography
            variant="h2"
            component="h2"
            textAlign="center"
            sx={{ color: "#2C2416", fontWeight: 700, mb: 2 }}
          >
            Our Recent Projects
          </Typography>
          <Typography
            variant="h6"
            textAlign="center"
            sx={{ color: "#5C4033", mb: 6 }}
          >
            Explore our portfolio of successful home transformations
          </Typography>

          <ProjectsGrid>
            {displayProjects.slice(0, visibleCount).map((project, i) => (
              <Box key={project._id || i} className="project-card-wrapper">
                <ProjectCard project={project} onClick={handleCardClick} />
              </Box>
            ))}
          </ProjectsGrid>

          {/* Load More / Show Less Buttons */}
          <Box sx={{ textAlign: "center", mt: 4 }}>
            {visibleCount < displayProjects.length && (
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#D2691E",
                  color: "#FFF",
                  mx: 1,
                  "&:hover": { backgroundColor: "#E08D4C" },
                }}
                endIcon={<ArrowForwardIcon />}
                onClick={() => setVisibleCount((c) => c + 6)}
              >
                Load More
              </Button>
            )}
            {visibleCount > 6 && (
              <Button
                variant="outlined"
                sx={{
                  color: "#D2691E",
                  borderColor: "#D2691E",
                  mx: 1,
                  "&:hover": { borderColor: "#E08D4C", color: "#E08D4C" },
                }}
                onClick={() => setVisibleCount(6)}
              >
                Show Less
              </Button>
            )}
          </Box>

          {/* CTA Below Projects */}
          <Box sx={{ textAlign: "center", mt: 6 }}>
            <CTAButton endIcon={<ArrowForwardIcon />} onClick={goToContactPage}>
              Get a Free Quote
            </CTAButton>
          </Box>
        </ProjectsContainer>
      </ProjectsSection>

      {/* Modal */}
      <ProjectModal
        open={modalOpen}
        handleClose={handleModalClose}
        project={selectedProject}
      />
    </>
  );
};

export default HomePage;
